<template>
  <div class="analises-card">
    <expandable-filters :open.sync="filtersExpanded" align="end">
      <template #leading>
        <h3 class="analises-title">Análises realizadas</h3>
        <p class="analises-subtitle">{{ subtitle }}</p>
      </template>

      <template #actions>
        <div class="tab-toggle chart-type-toggle">
          <button
            v-for="opt in chartTypeOptions"
            :key="opt.value"
            type="button"
            :class="['tab-toggle-btn', { active: chartType === opt.value }]"
            @click="chartType = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>

        <div class="tab-toggle period-toggle">
          <button
            type="button"
            :class="['tab-toggle-btn', { active: chartPeriod === 'dia' }]"
            :disabled="activeFilter !== 'range'"
            @click="period = 'dia'"
          >
            Dia
          </button>

          <button
            type="button"
            :class="['tab-toggle-btn', { active: chartPeriod === 'semana' }]"
            :disabled="activeFilter !== 'range'"
            @click="period = 'semana'"
          >
            Semana
          </button>

          <button
            type="button"
            :class="['tab-toggle-btn', { active: chartPeriod === 'mes' }]"
            :disabled="activeFilter !== 'range'"
            @click="period = 'mes'"
          >
            Mês
          </button>
        </div>
      </template>

      <select
        v-model.number="draftFilterYear"
        :class="['select-filtro', { 'chart-filter--locked': monthFilterLocked }]"
        aria-label="Ano"
        :disabled="monthFilterLocked"
      >
        <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
      </select>

      <div
        class="month-multiselect"
        :class="{ 'chart-filter--locked': monthFilterLocked }"
        v-click-outside="closeMonthsDropdown"
      >
        <button
          type="button"
          class="select-filtro month-multiselect-trigger"
          :aria-label="monthsDisplayText"
          :aria-expanded="monthsDropdownOpen ? 'true' : 'false'"
          :disabled="monthFilterLocked"
          @click="toggleMonthsDropdown"
        >
          <span class="month-multiselect-value">{{ monthsDisplayText }}</span>
          <span class="month-multiselect-caret" aria-hidden="true">▾</span>
        </button>
        <div
          v-show="monthsDropdownOpen"
          class="month-multiselect-menu"
          @mousedown.stop
        >
          <label :class="['month-option', { selected: allMonthsSelected }]">
            <input
              type="checkbox"
              :checked="allMonthsSelected"
              :disabled="monthFilterLocked"
              @change="toggleAllMonths"
            >
            Todos
          </label>
          <label
            v-for="month in monthOptions"
            :key="month.value"
            :class="['month-option', { selected: isMonthSelected(month.value) }]"
          >
            <input
              type="checkbox"
              :checked="isMonthSelected(month.value)"
              :disabled="monthFilterLocked"
              @change="toggleMonth(month.value)"
            >
            {{ month.label }}
          </label>
          <div class="month-multiselect-footer">
            <button
              type="button"
              class="btn-aplicar-filtro"
              :disabled="monthFilterLocked || !canApplyMonthFilter"
              @click="applyMonthFilter"
            >
              Aplicar filtro
            </button>
          </div>
        </div>
      </div>

      <div class="periodo-filtro" :class="{ 'chart-filter--locked': periodFilterLocked }">
        <date-range-picker
          :start-date.sync="dateRangeStart"
          :end-date.sync="dateRangeEnd"
          :min-date="pickerMinDate"
          :max-date="pickerMaxDate"
          :disabled="periodFilterLocked"
          placeholder="Selecione o período"
          @apply="onPeriodApply"
        />
      </div>

      <button
        class="btn-limpar"
        type="button"
        :disabled="!canClearFilters"
        @click="clearFilters"
      >
        Limpar filtros
      </button>
    </expandable-filters>

    <div v-if="errorMessage" class="error-banner">
      <span>{{ errorMessage }}</span>
      <button class="btn-fechar-erro" type="button" @click="errorMessage = ''">&times;</button>
    </div>

    <div v-if="isLoading" class="estado-vazio">
      <p>Carregando gráfico...</p>
    </div>

    <div v-else-if="labels.length === 0" class="estado-vazio">
      <p>Nenhuma análise encontrada no período.</p>
    </div>

    <div v-show="!isLoading && labels.length > 0" class="analises-chart-wrapper">
      <canvas ref="canvasRef"></canvas>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  BarController,
  BarElement,
  PieController,
  ArcElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
} from 'chart.js'
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot'
import { api } from '@/services'
import { DB_ROUTES } from '@/constants'
import ExpandableFilters from '@/components/static/ExpandableFilters.vue'

Chart.register(
  BarController,
  BarElement,
  PieController,
  ArcElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  BoxPlotController,
  BoxAndWiskers
)

function boxplotLabelCallback (ctx) {
  return boxplotTooltipLabels(ctx.raw || ctx.parsed)
}

BoxPlotController.prototype._toStringStats = function (stats) {
  return boxplotTooltipLabels(stats).join('\n')
}

const originalGetLabelAndValue = BoxPlotController.prototype.getLabelAndValue
BoxPlotController.prototype.getLabelAndValue = function (index) {
  const result = originalGetLabelAndValue.call(this, index)
  if (result && result.value && typeof result.value === 'object') {
    const lines = boxplotTooltipLabels(result.value.raw || result.value)
    result.value.toString = function () {
      return lines.join('\n')
    }
  }
  return result
}

if (Chart.overrides.boxplot && Chart.overrides.boxplot.plugins && Chart.overrides.boxplot.plugins.tooltip) {
  const tooltip = Chart.overrides.boxplot.plugins.tooltip
  tooltip.callbacks = Object.assign({}, tooltip.callbacks, {
    label: boxplotLabelCallback,
    beforeLabel () {
      return ''
    }
  })
}

const WEEKDAY_LABELS = {
  0: 'Dom',
  1: 'Seg',
  2: 'Ter',
  3: 'Qua',
  4: 'Qui',
  5: 'Sex',
  6: 'Sáb'
}
const MONTH_OPTIONS = [
  { value: 1, label: 'Janeiro', shortLabel: 'Jan' },
  { value: 2, label: 'Fevereiro', shortLabel: 'Fev' },
  { value: 3, label: 'Março', shortLabel: 'Mar' },
  { value: 4, label: 'Abril', shortLabel: 'Abr' },
  { value: 5, label: 'Maio', shortLabel: 'Mai' },
  { value: 6, label: 'Junho', shortLabel: 'Jun' },
  { value: 7, label: 'Julho', shortLabel: 'Jul' },
  { value: 8, label: 'Agosto', shortLabel: 'Ago' },
  { value: 9, label: 'Setembro', shortLabel: 'Set' },
  { value: 10, label: 'Outubro', shortLabel: 'Out' },
  { value: 11, label: 'Novembro', shortLabel: 'Nov' },
  { value: 12, label: 'Dezembro', shortLabel: 'Dez' }
]
const PIE_COLORS = ['#6C63FF', '#C2185B', '#00BFA5', '#7C4DFF', '#FF8A65', '#FFA726', '#FF6D00']

function getBucketCount (bucket) {
  return Number((bucket && (bucket.count ?? bucket.Count)) || 0)
}

function dailyCountsBetween (buckets, start, end) {
  return eachDayInclusive(start, end).map(date => getBucketCount(buckets[toDayKey(date)]))
}

function weeklyCountsBetween (buckets, start, end) {
  const order = []
  const groups = {}
  eachDayInclusive(start, end).forEach(date => {
    const key = isoWeekKey(date)
    if (!groups[key]) {
      groups[key] = []
      order.push(key)
    }
    groups[key].push(getBucketCount(buckets[toDayKey(date)]))
  })
  return order.map(key => groups[key].reduce((sum, value) => sum + value, 0))
}

function activeCounts (values) {
  const positive = (Array.isArray(values) ? values : []).map(Number).filter(n => n > 0)
  return positive.length ? positive : [0]
}

function formatCountLabel (value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} análises`
}

function quartile (sorted, q) {
  if (!sorted.length) return 0
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  if (sorted[base + 1] === undefined) return sorted[base]
  return sorted[base] + rest * (sorted[base + 1] - sorted[base])
}

function boxplotFromCounts (values) {
  const nums = (Array.isArray(values) ? values : [values])
    .map(Number)
    .filter(n => Number.isFinite(n))
    .sort((a, b) => a - b)

  if (!nums.length) {
    return { min: 0, q1: 0, median: 0, q3: 0, max: 0, mean: 0 }
  }

  const mean = nums.reduce((sum, value) => sum + value, 0) / nums.length
  return {
    min: nums[0],
    q1: quartile(nums, 0.25),
    median: quartile(nums, 0.5),
    q3: quartile(nums, 0.75),
    max: nums[nums.length - 1],
    mean
  }
}

function boxplotTooltipLabels (stats) {
  if (!stats) return []
  const source = Array.isArray(stats) ? boxplotFromCounts(stats) : stats
  return [
    `Mínimo: ${formatCountLabel(source.min)}`,
    `1º quartil: ${formatCountLabel(source.q1)}`,
    `Mediana: ${formatCountLabel(source.median)}`,
    `3º quartil: ${formatCountLabel(source.q3)}`,
    `Máximo: ${formatCountLabel(source.max)}`
  ]
}

function toIsoDate (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseIsoDate (value) {
  const [year, month, day] = String(value).split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toDayKey (date) {
  return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
}

function parseDayKey (key) {
  const [day, month, year] = String(key).split('-').map(Number)
  if (!day || !month || !year) return null
  return new Date(year, month - 1, day)
}

function formatShortDate (date) {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}`
}

function todayDate () {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function normalizeMonths (months) {
  const list = (Array.isArray(months) ? months : [months])
    .map(Number)
    .filter(month => month >= 1 && month <= 12)
  const unique = Array.from(new Set(list)).sort((a, b) => a - b)
  return unique.length ? unique : [todayDate().getMonth() + 1]
}

function monthDateBounds (year, months) {
  const selected = normalizeMonths(months)
  const start = new Date(year, selected[0] - 1, 1)
  const end = new Date(year, selected[selected.length - 1], 0)
  const todayIso = toIsoDate(todayDate())
  const startDate = toIsoDate(start)
  let endDate = toIsoDate(end)

  if (startDate > todayIso) {
    return { startDate: todayIso, endDate: todayIso }
  }
  if (endDate > todayIso) {
    endDate = todayIso
  }
  return { startDate, endDate }
}

function maxSelectableBounds () {
  const today = todayDate()
  const start = new Date(today.getFullYear() - 5, 0, 1)
  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(today)
  }
}

function isoWeekKey (date) {
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = utc.getUTCDay() || 7
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil((((utc - yearStart) / 86400000) + 1) / 7)
  return `${weekNo}-${utc.getUTCFullYear()}`
}

function mondayOfIsoWeek (week, isoYear) {
  const jan4 = new Date(Date.UTC(isoYear, 0, 4))
  const day = jan4.getUTCDay() || 7
  const monday = new Date(jan4)
  monday.setUTCDate(jan4.getUTCDate() - day + 1 + (week - 1) * 7)
  return new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate())
}

function formatWeekRangeLabel (key, chartRange) {
  const [week, isoYear] = String(key).split('-').map(Number)
  if (!week || !isoYear) return key

  let start = mondayOfIsoWeek(week, isoYear)
  let end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)

  if (chartRange && chartRange.startDate && chartRange.endDate) {
    const rangeStart = parseIsoDate(chartRange.startDate)
    const rangeEnd = parseIsoDate(chartRange.endDate)
    if (start < rangeStart) start = rangeStart
    if (end > rangeEnd) end = rangeEnd
  }

  if (toIsoDate(start) === toIsoDate(end)) {
    return formatShortDate(start)
  }
  return `${formatShortDate(start)} a ${formatShortDate(end)}`
}

function eachDayInclusive (start, end) {
  const days = []
  const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  for (let offset = 0; offset <= 366; offset += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset)
    if (date > endDate) break
    days.push(date)
  }
  return days
}

function monthBucketKey (month, year) {
  return `${month}-${year}`
}

function eachMonthInclusive (start, end) {
  const months = []
  const startIndex = start.getFullYear() * 12 + start.getMonth()
  const endIndex = end.getFullYear() * 12 + end.getMonth()
  for (let offset = 0; offset <= 24; offset += 1) {
    const index = startIndex + offset
    if (index > endIndex) break
    const year = Math.floor(index / 12)
    const month = (index % 12) + 1
    months.push({
      year,
      month,
      key: monthBucketKey(month, year)
    })
  }
  return months
}

function averageDurationFromBuckets (buckets) {
  let totalCount = 0
  let weighted = 0
  Object.values(buckets || {}).forEach(bucket => {
    const count = Number(bucket.count ?? bucket.Count ?? 0)
    const stats = bucket.durationSeconds || bucket.DurationSeconds || {}
    const mean = Number(stats.mean ?? stats.Mean ?? 0)
    if (!count) return
    totalCount += count
    weighted += mean * count
  })
  return totalCount > 0 ? weighted / totalCount : 0
}

export default {
  name: 'AnalisesChart',
  components: {
    ExpandableFilters
  },
  directives: {
    'click-outside': {
      bind (el, binding) {
        el._clickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('mousedown', el._clickOutside)
      },
      unbind (el) {
        document.removeEventListener('mousedown', el._clickOutside)
      }
    }
  },
  props: {
    selectedYear: {
      type: Number,
      required: true
    }
  },
  emits: ['period-change'],
  data () {
    const today = todayDate()
    const year = this.selectedYear || today.getFullYear()
    const month = today.getMonth() + 1
    const bounds = monthDateBounds(year, month)
    return {
      period: 'semana',
      chartType: 'bar',
      chartTypeOptions: [
        { value: 'bar', label: 'Barras' },
        { value: 'boxplot', label: 'Box Plot' },
        { value: 'pie', label: 'Pizza' }
      ],
      monthOptions: MONTH_OPTIONS,
      filterYear: year,
      filterMonths: [month],
      draftFilterYear: year,
      draftFilterMonths: [month],
      monthsDropdownOpen: false,
      filtersExpanded: false,
      chartLoadQueued: false,
      activeFilter: 'month',
      clearingFilters: false,
      dateRangeStart: bounds.startDate,
      dateRangeEnd: bounds.endDate,
      chartInstance: null,
      isLoading: false,
      errorMessage: '',
      averagePerDay: 0,
      buckets: {}
    }
  },
  computed: {
    yearOptions () {
      const currentYear = todayDate().getFullYear()
      const years = []
      for (let year = currentYear - 5; year <= currentYear; year += 1) {
        years.push(year)
      }
      return years
    },
    monthBounds () {
      return monthDateBounds(this.filterYear, this.filterMonths)
    },
    monthFilterLocked () {
      return this.activeFilter === 'range'
    },
    periodFilterLocked () {
      return this.activeFilter === 'month'
    },
    pickerMinDate () {
      if (this.activeFilter === 'month') return this.monthBounds.startDate
      return maxSelectableBounds().startDate
    },
    pickerMaxDate () {
      if (this.activeFilter === 'month') return this.monthBounds.endDate
      return maxSelectableBounds().endDate
    },
    canClearFilters () {
      return this.activeFilter !== null
    },
    visibleMonths () {
      return this.monthsDropdownOpen
        ? normalizeMonths(this.draftFilterMonths)
        : normalizeMonths(this.filterMonths)
    },
    allMonthsSelected () {
      return this.visibleMonths.length === MONTH_OPTIONS.length
    },
    canApplyMonthFilter () {
      if (this.monthFilterLocked) return false
      const draftMonths = normalizeMonths(this.draftFilterMonths).join(',')
      const appliedMonths = normalizeMonths(this.filterMonths).join(',')
      return this.draftFilterYear !== this.filterYear ||
        draftMonths !== appliedMonths ||
        this.activeFilter !== 'month'
    },
    monthsDisplayText () {
      if (this.activeFilter === 'range' && !this.monthsDropdownOpen) {
        const start = parseIsoDate(this.chartRange.startDate)
        const end = parseIsoDate(this.chartRange.endDate)
        const months = eachMonthInclusive(start, end)
        if (!months.length) return 'Período'
        if (months.length === 1) {
          const option = this.monthOptions.find(item => item.value === months[0].month)
          return option ? option.label : 'Período'
        }
        const first = this.monthOptions.find(item => item.value === months[0].month)
        const last = this.monthOptions.find(item => item.value === months[months.length - 1].month)
        return `${first ? first.shortLabel : ''} a ${last ? last.shortLabel : ''}`.trim()
      }
      const selected = this.visibleMonths
      if (selected.length === MONTH_OPTIONS.length) return 'Todos'
      const options = selected.map(month => this.monthOptions.find(item => item.value === month)).filter(Boolean)
      if (options.length === 1) return options[0].label
      const contiguous = selected.every((month, index) => index === 0 || month === selected[index - 1] + 1)
      const labels = options.map(item => item.shortLabel)
      if (contiguous) return `${labels[0]} a ${labels[labels.length - 1]}`
      if (labels.length <= 3) return labels.join(', ')
      return `${labels.length} meses`
    },
    chartPeriod () {
      if (this.activeFilter !== 'range') {
        return normalizeMonths(this.filterMonths).length > 1 ? 'mes' : 'semana'
      }
      return this.period
    },
    chartRange () {
      const bounds = this.monthBounds
      const startDate = this.dateRangeStart || bounds.startDate
      const endDate = this.dateRangeEnd || this.dateRangeStart || bounds.endDate
      return { startDate, endDate }
    },
    filterKey () {
      return `${this.chartPeriod}|${this.chartRange.startDate}|${this.chartRange.endDate}|${this.filterMonths.join(',')}`
    },
    selectedMonthsForDuration () {
      if (this.activeFilter === 'range') {
        const start = parseIsoDate(this.chartRange.startDate)
        const end = parseIsoDate(this.chartRange.endDate)
        return eachMonthInclusive(start, end)
          .map(item => {
            const option = MONTH_OPTIONS.find(month => month.value === item.month)
            return option ? { label: option.label, year: item.year } : null
          })
          .filter(Boolean)
      }

      const year = this.filterYear
      return normalizeMonths(this.filterMonths)
        .map(month => {
          const option = MONTH_OPTIONS.find(item => item.value === month)
          return option ? { label: option.label, year } : null
        })
        .filter(Boolean)
    },
    subtitle () {
      const start = formatShortDate(parseIsoDate(this.chartRange.startDate))
      const end = formatShortDate(parseIsoDate(this.chartRange.endDate))
      const average = `Média de ${Number(this.averagePerDay || 0).toFixed(1)} por dia.`
      const rangeText = this.chartRange.startDate === this.chartRange.endDate
        ? `${start}. ${average}`
        : `${start} a ${end}. ${average}`
      if (this.chartType !== 'boxplot' || this.chartPeriod === 'dia') return rangeText
      const unit = this.chartPeriod === 'mes' ? 'semana' : 'dia com análise'
      return `${rangeText} A caixa mostra a variação por ${unit}; o número acima é o total.`
    },
    orderedEntries () {
      const start = parseIsoDate(this.chartRange.startDate)
      const end = parseIsoDate(this.chartRange.endDate)
      const buckets = this.buckets || {}

      if (this.chartPeriod === 'mes') {
        const selected = new Set(normalizeMonths(this.filterMonths))
        return eachMonthInclusive(start, end)
          .filter(item => this.activeFilter === 'range' || selected.has(item.month))
          .map(item => {
            const monthStart = new Date(item.year, item.month - 1, 1)
            const monthEnd = new Date(item.year, item.month, 0)
            const rangeStart = monthStart < start ? start : monthStart
            const rangeEnd = monthEnd > end ? end : monthEnd
            const dailyCounts = rangeStart > rangeEnd
              ? []
              : dailyCountsBetween(buckets, rangeStart, rangeEnd)
            const weeklyCounts = rangeStart > rangeEnd
              ? []
              : weeklyCountsBetween(buckets, rangeStart, rangeEnd)
            const count = dailyCounts.reduce((sum, value) => sum + value, 0)
            return [item.key, { count, dailyCounts, weeklyCounts }]
          })
      }

      if (this.chartPeriod === 'semana') {
        const keys = []
        const seen = {}
        eachDayInclusive(start, end).forEach(date => {
          const key = isoWeekKey(date)
          if (!seen[key]) {
            seen[key] = true
            keys.push(key)
          }
        })
        return keys.map(key => {
          const [week, isoYear] = String(key).split('-').map(Number)
          let weekStart = mondayOfIsoWeek(week, isoYear)
          let weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6)
          if (weekStart < start) weekStart = start
          if (weekEnd > end) weekEnd = end
          const dailyCounts = dailyCountsBetween(buckets, weekStart, weekEnd)
          const count = dailyCounts.reduce((sum, value) => sum + value, 0)
          return [key, { count, dailyCounts }]
        })
      }

      return eachDayInclusive(start, end).map(date => {
        const key = toDayKey(date)
        const count = getBucketCount(buckets[key])
        return [key, { count, dailyCounts: [count] }]
      })
    },
    labels () {
      return this.orderedEntries.map(([key]) => this.formatBucketLabel(key))
    },
    counts () {
      return this.orderedEntries.map(([, bucket]) => getBucketCount(bucket))
    },
    share () {
      const total = this.counts.reduce((sum, value) => sum + value, 0)
      if (!total) return this.counts.map(() => 0)
      return this.counts.map(value => Math.round((value / total) * 100))
    },
    boxplotSeries () {
      return this.orderedEntries.map(([, bucket]) => {
        if (this.chartPeriod === 'mes') return activeCounts(bucket.weeklyCounts)
        if (this.chartPeriod === 'dia') {
          return [getBucketCount(bucket)]
        }
        return activeCounts(bucket.dailyCounts)
      })
    },
    boxplotAxisTitle () {
      if (this.chartPeriod === 'mes') return 'Análises por semana'
      if (this.chartPeriod === 'semana') return 'Análises por dia'
      return 'Quantidade de análises'
    }
  },
  watch: {
    selectedYear (year) {
      this.filterYear = year
      this.draftFilterYear = year
    },
    filterKey () {
      this.queueChartLoad()
    },
    chartType () {
      this.renderChart()
    },
    filtersExpanded (isOpen) {
      if (!isOpen) this.closeMonthsDropdown()
    }
  },
  mounted () {
    this.loadChart()
  },
  beforeDestroy () {
    if (this.chartInstance) this.chartInstance.destroy()
  },
  methods: {
    resetRangeToMonth () {
      const bounds = monthDateBounds(this.filterYear, this.filterMonths)
      this.dateRangeStart = bounds.startDate
      this.dateRangeEnd = bounds.endDate
    },
    activateMonthFilter () {
      this.activeFilter = 'month'
    },
    onPeriodApply () {
      this.activeFilter = 'range'
      this.syncMonthDrafts()
      this.closeMonthsDropdown()
    },
    defaultMonthSelection () {
      const today = todayDate()
      const year = this.selectedYear || today.getFullYear()
      const month = today.getMonth() + 1
      return { year, month }
    },
    syncMonthDrafts () {
      this.draftFilterYear = this.filterYear
      this.draftFilterMonths = normalizeMonths(this.filterMonths)
    },
    applyMonthFilter () {
      const selected = normalizeMonths(this.draftFilterMonths)
      if (this.monthFilterLocked || !selected.length) return
      this.filterYear = this.draftFilterYear
      this.filterMonths = selected
      this.activateMonthFilter()
      this.resetRangeToMonth()
      this.closeMonthsDropdown()
    },
    clearFilters () {
      const { year, month } = this.defaultMonthSelection()
      this.clearingFilters = true
      this.activeFilter = null
      this.filterYear = year
      this.filterMonths = [month]
      this.draftFilterYear = year
      this.draftFilterMonths = [month]
      this.closeMonthsDropdown()
      this.resetRangeToMonth()
      this.$nextTick(() => {
        this.clearingFilters = false
      })
    },
    queueChartLoad () {
      if (this.monthsDropdownOpen) {
        this.chartLoadQueued = true
        return
      }
      this.loadChart()
    },
    flushChartLoad () {
      if (!this.chartLoadQueued) return
      this.chartLoadQueued = false
      this.loadChart()
    },
    closeMonthsDropdown () {
      if (!this.monthsDropdownOpen) return
      this.monthsDropdownOpen = false
      this.flushChartLoad()
    },
    toggleMonthsDropdown () {
      if (this.monthFilterLocked) return
      if (this.monthsDropdownOpen) {
        this.closeMonthsDropdown()
        return
      }
      if (!this.canApplyMonthFilter) {
        this.syncMonthDrafts()
      }
      this.monthsDropdownOpen = true
    },
    isMonthSelected (month) {
      return this.visibleMonths.includes(Number(month))
    },
    toggleMonth (month) {
      const value = Number(month)
      const current = normalizeMonths(this.draftFilterMonths)
      const selected = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value].sort((a, b) => a - b)
      if (!selected.length) return
      this.draftFilterMonths = selected
    },
    toggleAllMonths () {
      if (this.allMonthsSelected) {
        this.draftFilterMonths = [todayDate().getMonth() + 1]
        return
      }
      this.draftFilterMonths = this.monthOptions.map(option => option.value)
    },
    formatBucketLabel (key) {
      if (this.chartPeriod === 'semana') {
        return formatWeekRangeLabel(key, this.chartRange)
      }
      if (this.chartPeriod === 'mes') {
        const month = Number(String(key).split('-')[0])
        const option = this.monthOptions.find(item => item.value === month)
        return option ? option.label : key
      }
      const date = parseDayKey(key)
      if (!date) return key
      const weekday = WEEKDAY_LABELS[date.getDay()] || ''
      return `${weekday} ${formatShortDate(date)}`
    },
    buildBarConfig () {
      const counts = this.counts
      const share = this.share
      const dense = this.labels.length > 12

      return {
        type: 'bar',
        data: {
          labels: this.labels,
          datasets: [
            {
              data: counts,
              backgroundColor: '#FF6B00',
              borderRadius: 4,
              maxBarThickness: 100,
              categoryPercentage: 0.9,
              barPercentage: 0.9
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { top: 22 }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label (ctx) {
                  const count = ctx.parsed.y
                  const pct = share[ctx.dataIndex] || 0
                  return `${count} análises (${pct}%)`
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { precision: 0 },
              title: { display: true, text: 'Quantidade de análises' }
            },
            x: {
              grid: { display: false },
              ticks: {
                autoSkip: this.period !== 'semana',
                font: { size: this.chartPeriod === 'semana' ? 11 : 12 }
              }
            }
          }
        },
        plugins: [
          {
            id: 'barDataLabels',
            afterDatasetsDraw (chart) {
              const { ctx } = chart
              chart.getDatasetMeta(0).data.forEach((bar, i) => {
                const count = counts[i]
                const pct = share[i] || 0
                if (!count) return

                const height = Math.abs(bar.base - bar.y)
                ctx.save()
                ctx.textAlign = 'center'
                ctx.font = dense ? 'bold 11px sans-serif' : 'bold 12px sans-serif'

                if (!dense && height > 38) {
                  ctx.fillStyle = '#fff'
                  ctx.textBaseline = 'middle'
                  ctx.fillText(String(count), bar.x, bar.y + height / 2 - 7)
                  ctx.font = '600 11px sans-serif'
                  ctx.fillText(`${pct}%`, bar.x, bar.y + height / 2 + 8)
                } else {
                  ctx.fillStyle = '#1f2937'
                  ctx.textBaseline = 'bottom'
                  ctx.fillText(dense ? String(count) : `${count} (${pct}%)`, bar.x, bar.y - 4)
                }
                ctx.restore()
              })
            }
          }
        ]
      }
    },
    buildBoxplotConfig () {
      const counts = this.counts
      const share = this.share
      const series = this.boxplotSeries
      const unit = this.chartPeriod === 'mes' ? 'semana' : 'dia'
      const labelCallback = (ctx) => {
        const total = counts[ctx.dataIndex] || 0
        const pct = share[ctx.dataIndex] || 0
        const values = series[ctx.dataIndex] || []
        return [
          `Total: ${total} análises (${pct}%)`,
          ...boxplotTooltipLabels(boxplotFromCounts(values)).map(line => `${line} por ${unit}`)
        ]
      }
      const tooltipCallbacks = {
        beforeLabel () {
          return ''
        },
        label: labelCallback,
        afterLabel () {
          return ''
        }
      }

      return {
        type: 'boxplot',
        data: {
          labels: this.labels,
          datasets: [
            {
              data: series,
              backgroundColor: 'rgba(255, 107, 0, 0.28)',
              borderColor: '#FF6B00',
              borderWidth: 2,
              medianColor: '#9a3412',
              itemRadius: 4,
              itemBackgroundColor: '#FF6B00',
              itemBorderColor: '#ffffff',
              itemBorderWidth: 1,
              outlierRadius: 4,
              outlierBackgroundColor: '#FF6B00',
              meanRadius: 0,
              barPercentage: 0.55,
              categoryPercentage: 0.7,
              tooltip: {
                callbacks: tooltipCallbacks
              }
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { top: 22 }
          },
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              displayColors: false,
              callbacks: tooltipCallbacks
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grace: '18%',
              ticks: { precision: 0 },
              title: { display: true, text: this.boxplotAxisTitle }
            }
          }
        },
        plugins: [
          {
            id: 'boxplotTotals',
            afterDatasetsDraw (chart) {
              const { ctx } = chart
              const meta = chart.getDatasetMeta(0)
              meta.data.forEach((box, i) => {
                const total = counts[i]
                if (!total || !box) return
                ctx.save()
                ctx.fillStyle = '#1f2937'
                ctx.font = 'bold 12px sans-serif'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'bottom'
                ctx.fillText(String(total), box.x, box.y - 6)
                ctx.restore()
              })
            }
          },
          {
            id: 'boxplotPtTooltip',
            afterInit (chart) {
              if (!chart.options.plugins.tooltip) {
                chart.options.plugins.tooltip = {}
              }
              if (!chart.options.plugins.tooltip.callbacks) {
                chart.options.plugins.tooltip.callbacks = {}
              }
              chart.options.plugins.tooltip.callbacks.beforeLabel = function () {
                return ''
              }
              chart.options.plugins.tooltip.callbacks.label = labelCallback
              chart.options.plugins.tooltip.callbacks.afterLabel = function () {
                return ''
              }
            }
          }
        ]
      }
    },
    buildPieConfig () {
      const counts = this.counts
      const share = this.share

      return {
        type: 'pie',
        data: {
          labels: this.labels,
          datasets: [
            {
              data: counts,
              backgroundColor: PIE_COLORS.slice(0, this.labels.length),
              borderWidth: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top' },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const count = counts[ctx.dataIndex] || 0
                  const pct = share[ctx.dataIndex] || 0
                  return `${ctx.label}: ${count} análises (${pct}%)`
                }
              }
            }
          }
        },
        plugins: [
          {
            id: 'pieDataLabels',
            afterDatasetsDraw (chart) {
              const { ctx } = chart
              chart.getDatasetMeta(0).data.forEach((arc, i) => {
                const count = counts[i]
                if (!count) return
                const pos = arc.tooltipPosition()
                ctx.save()
                ctx.fillStyle = '#fff'
                ctx.font = 'bold 13px sans-serif'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText(String(count), pos.x, pos.y)
                ctx.restore()
              })
            }
          }
        ]
      }
    },
    renderChart () {
      const canvas = this.$refs.canvasRef
      if (!canvas || this.labels.length === 0) {
        if (this.chartInstance) {
          this.chartInstance.destroy()
          this.chartInstance = null
        }
        return
      }

      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }

      let config
      if (this.chartType === 'bar') config = this.buildBarConfig()
      else if (this.chartType === 'boxplot') config = this.buildBoxplotConfig()
      else config = this.buildPieConfig()

      this.chartInstance = new Chart(canvas, config)
    },
    async loadChart () {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const { startDate, endDate } = this.chartRange
        const response = await api.get(DB_ROUTES.GET_ANALYSIS_DASHBOARD_CHART({
          startDate,
          endDate,
          groupBy: 'day'
        }))

        const data = response.data || {}
        this.averagePerDay = data.averagePerDay || 0
        this.buckets = data.buckets || {}
        this.$emit('period-change', {
          startDate,
          endDate,
          averageDurationSeconds: averageDurationFromBuckets(this.buckets),
          selectedMonths: this.selectedMonthsForDuration
        })
      } catch (error) {
        console.error('Erro ao carregar gráfico:', error)
        this.averagePerDay = 0
        this.buckets = {}
        this.errorMessage = (error.response && error.response.data && error.response.data.message) ||
          'Não foi possível carregar o gráfico.'
        this.$emit('period-change', {
          startDate: this.chartRange.startDate,
          endDate: this.chartRange.endDate,
          averageDurationSeconds: 0,
          selectedMonths: this.selectedMonthsForDuration
        })
      } finally {
        this.isLoading = false
        this.$nextTick(() => this.renderChart())
      }
    }
  }
}
</script>

<style scoped>
.analises-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  overflow: visible;
}

.analises-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.analises-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.month-multiselect {
  position: relative;
  min-width: 180px;
}

.month-multiselect-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  text-align: left;
  font-family: inherit;
  font-weight: 600;
}

.month-multiselect-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.month-multiselect-caret {
  color: #9ca3af;
  font-size: 11px;
  line-height: 1;
}

.month-multiselect-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: auto;
  right: 0;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 1000;
}

.month-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.month-option input {
  width: 15px;
  height: 15px;
  margin: 0;
  accent-color: #FF6B00;
  cursor: pointer;
}

.month-option:hover,
.month-option.selected {
  background: #FFF5EF;
}

.month-multiselect-footer {
  position: sticky;
  bottom: 0;
  padding: 10px 12px;
  background: #ffffff;
  border-top: 1px solid #EFEFEF;
}

.btn-aplicar-filtro {
  width: 100%;
  background: #FF6B00;
  border: none;
  border-radius: 6px;
  min-height: 36px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
}

.btn-aplicar-filtro:hover:not(:disabled) {
  background: #e85f00;
}

.btn-aplicar-filtro:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.chart-filter--locked {
  opacity: 0.55;
}

.tab-toggle {
  display: flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 3px;
}

.tab-toggle-btn {
  border: none;
  background: transparent;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.tab-toggle-btn.active {
  background: #ffffff;
  color: #1f2937;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.tab-toggle-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.chart-type-toggle .tab-toggle-btn {
  padding: 6px 12px;
}

.analises-chart-wrapper {
  width: 100%;
  height: 300px;
}

.period-toggle .tab-toggle-btn {
  padding: 4px 16px;
  font-size: 12px;
}

.error-banner {
  background-color: #FEE2E2;
  color: #991B1B;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.btn-fechar-erro {
  background: none;
  border: none;
  color: #991B1B;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}

.estado-vazio {
  padding: 40px 0;
  text-align: center;
  color: #888888;
  font-size: 14px;
}
</style>
